import { useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Edit3, Globe, EyeOff, Copy, Download, Upload, Trash2, MoreVertical, Sparkles, Clock,
} from 'lucide-react';
import { api, unwrap, downloadFile } from '@/lib/api';
import { pickLocalized, type Localized } from '@/lib/i18n';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu';

interface PageSummary {
  id: string;
  slug: string;
  locale: string;
  title: Localized;
  status: 'draft' | 'published' | 'archived' | 'scheduled';
  updated_at: string;
}

export const Route = createFileRoute('/_app/pages/')({
  component: PagesListPage,
});

function PagesListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pages'],
    queryFn: async () => unwrap<{ data: PageSummary[] }>(api.get('/pages?per_page=50')),
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post(`/pages/${id}/publish`),
    onSuccess: () => {
      toast.success('Página publicada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post(`/pages/${id}/unpublish`),
    onSuccess: () => {
      toast.success('Página despublicada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => unwrap<PageSummary>(api.post(`/pages/${id}/duplicate`)),
    onSuccess: () => {
      toast.success('Página duplicada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/pages/${id}`),
    onSuccess: () => {
      toast.success('Página eliminada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const importPage = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text();
      const payload = JSON.parse(text);
      return unwrap<PageSummary>(api.post('/pages/import', payload));
    },
    onSuccess: (p) => {
      toast.success('Página importada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
      void navigate({ to: '/pages/$id', params: { id: p.id } });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Error al importar'),
  });

  async function exportPage(id: string, slug: string) {
    try {
      await downloadFile(`/pages/${id}/export`, `page-${slug}.json`);
    } catch {
      toast.error('No se pudo exportar');
    }
  }

  return (
    <div className="p-8">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Páginas</h1>
          <p className="text-sm text-neutral-500">Todas las páginas del sitio.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importPage.mutate(f);
              e.target.value = '';
            }}
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload className="size-4" /> Importar JSON
          </Button>
          <Button variant="outline" onClick={() => navigate({ to: '/page-templates' })}>
            <Sparkles className="size-4" /> Desde plantilla
          </Button>
          <Link
            to="/pages/$id"
            params={{ id: 'new' }}
            className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800"
          >
            <Plus className="size-4" /> Nueva página
          </Link>
        </div>
      </header>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Idioma</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Actualizada</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td className="px-4 py-6 text-center text-neutral-400" colSpan={6}>Cargando…</td></tr>
            )}
            {data?.data?.map((p) => (
              <tr key={p.id} className="border-b border-neutral-100 last:border-0">
                <td className="px-4 py-3 font-medium">{pickLocalized(p.title, p.locale, p.slug)}</td>
                <td className="px-4 py-3 text-neutral-500">/{p.slug}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 rounded bg-neutral-100 px-2 py-0.5 text-xs">{p.locale}</span>
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-neutral-500">{new Date(p.updated_at).toLocaleString('es')}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <Link
                      to="/pages/$id"
                      params={{ id: p.id }}
                      className="inline-flex items-center gap-1 rounded border border-neutral-200 px-2 py-1 text-xs hover:bg-neutral-50"
                    >
                      <Edit3 className="size-3" /> Editar
                    </Link>
                    {p.status === 'published' ? (
                      <button
                        onClick={() => unpublish.mutate(p.id)}
                        className="inline-flex items-center gap-1 rounded border border-neutral-200 px-2 py-1 text-xs hover:bg-neutral-50"
                      >
                        <EyeOff className="size-3" /> Despublicar
                      </button>
                    ) : (
                      <button
                        onClick={() => publish.mutate(p.id)}
                        className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700"
                      >
                        <Globe className="size-3" /> Publicar
                      </button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded border border-neutral-200 p-1 text-neutral-500 hover:bg-neutral-50" aria-label="Más">
                          <MoreVertical className="size-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => duplicate.mutate(p.id)}>
                          <Copy className="mr-2 size-4" /> Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => exportPage(p.id, p.slug)}>
                          <Download className="mr-2 size-4" /> Exportar JSON
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-700"
                          onClick={() => {
                            if (confirm(`¿Eliminar "${pickLocalized(p.title, p.locale, p.slug)}"?`)) {
                              remove.mutate(p.id);
                            }
                          }}
                        >
                          <Trash2 className="mr-2 size-4" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: PageSummary['status'] }) {
  const styles: Record<PageSummary['status'], string> = {
    draft: 'bg-neutral-100 text-neutral-700',
    published: 'bg-emerald-100 text-emerald-700',
    archived: 'bg-amber-100 text-amber-700',
    scheduled: 'bg-blue-100 text-blue-700',
  };
  const labels: Record<PageSummary['status'], string> = {
    draft: 'Borrador',
    published: 'Publicada',
    archived: 'Archivada',
    scheduled: 'Programada',
  };
  const icon = status === 'scheduled' ? <Clock className="mr-1 inline size-3" /> : null;
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {icon}{labels[status]}
    </span>
  );
}
